import { formatMessageOutsideReact } from '@/utils/i18n/intl-bridge';
import { extractAxiosErrorDetail } from '@heidi/api/errors';
import type { ToastCopyMeta, ToastCopySource } from '@heidi/api/meta';
import { toast } from '@heidi/design-system/toast';
import {
  STUCK_SESSION_CREATION_MUTATION_KEYS,
  TEAM_MEMBERSHIP_GATED_SESSION_QUERY_KEY,
} from '@heidi/session/contracts';
import { GET_JOINED_TEAMS } from '@heidi/workspace/contracts';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import i18next from 'i18next';

const GET_GARBAGE_COLLECTION_TIME = () => {
  return 1000 * 60 * 10; // 10 minutes
};

// Query keys for endpoints that require team membership. A 403 on any of these
// means the user has been removed from the team (or never had access); either
// way the joined-teams list is the source of truth and must be re-fetched so
// the UI no longer shows the inaccessible team. Includes the team-member
// session list, which is the request that 403s for a removed user actively
// reviewing a teammate's sessions. See APP-5396.
const TEAM_MEMBERSHIP_GATED_QUERY_KEYS = new Set([
  'getTeamBasicDetails',
  'getTeamDetails',
  'getTeamMembersWithRelationship',
  // Read from session's own contracts rather than inlined, so the global
  // client no longer embeds another domain's key spelling (task C1).
  TEAM_MEMBERSHIP_GATED_SESSION_QUERY_KEY,
]);

const isTeamMembershipGatedQuery = (queryKey: QueryKey): boolean => {
  const firstKey = queryKey[0];
  return typeof firstKey === 'string' && TEAM_MEMBERSHIP_GATED_QUERY_KEYS.has(firstKey);
};

const isForbiddenError = (error: unknown): boolean => {
  return error instanceof AxiosError && error.response?.status === 403;
};

// ────────────────────────────────────────────────────────────────────────────
// THE ONE SUBSCRIBER that turns `meta` toast copy into a toast (ARCFE-1077
// decision 12 option 1; extended to queries + three copy channels by
// ARCFE-1082).
//
// This is the whole app-side half of the mechanism. Because it lives on the
// shared `MutationCache`/`QueryCache`, a package hook can declare user-visible
// copy as inert data on its `useMutation({ meta })` / `useQuery({ meta })` and
// stay headless — no `@heidi/design-system/toast` import anywhere in a package
// `/data` zone. The old-path delegate that used to own the toast dissolves to
// a pure re-export shim.
//
// STRICTLY OPT-IN, and that is the safety property. These handlers run for
// EVERY mutation and EVERY query in the app, including the several hundred
// that know nothing about them. No `meta` — or `meta` carrying other keys —
// takes the early return and contributes nothing, so switching this on cannot
// add a toast anywhere a toast was not explicitly declared.
//
// The runtime guards are not redundant with the types. `meta` survives
// dehydration through `PersistQueryClientProvider` as plain JSON, so a blob
// written by an older build can rehydrate into a shape the current types
// promise but the current code has never seen. The guards make that a no-op
// rather than an `undefined is not an object` inside a cache callback, which
// would surface as a broken mutation rather than a missing toast.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Turn one `ToastCopySource` into the string a toast shows (ARCFE-1082 v1.1).
 *
 * Three channels, discriminated structurally rather than by a tag field so a
 * package can write a `defineMessages()` member straight into `meta` with no
 * wrapper:
 *
 *   - `string`            — literal copy, used as-is (the v1.0 behaviour).
 *   - `{ i18nextKey }`    — legacy i18next. `i18next.t` is called on the
 *     module singleton, exactly as `src/tauri/errors/index.ts` does from
 *     non-React code. DEPRECATED — see `ToastI18nextCopy`.
 *   - anything else       — a react-intl `MessageDescriptor`, formatted via
 *     `formatMessageOutsideReact`, which falls back to `defaultMessage` until
 *     `HeidiIntlProvider` has published an `IntlShape` (early boot, jest).
 *
 * The i18next branch is tested BEFORE the descriptor branch because a
 * descriptor never carries an `i18nextKey`, whereas `in` on a descriptor is
 * cheap and total. Order matters only for a malformed object, which the
 * caller's guard has already rejected.
 */
const resolveCopy = (source: ToastCopySource): string => {
  if (typeof source === 'string') return source;
  if ('i18nextKey' in source) {
    // The generated `TranslationKeys` union is an app type the contract cannot
    // name (packages may not import `@/types/**`), so the narrowing happens
    // here, at the one boundary that knows both sides.
    return i18next.t(source.i18nextKey);
  }
  return formatMessageOutsideReact(source);
};

/** A shape the resolver can actually turn into a string. */
const isCopySource = (value: unknown): value is ToastCopySource =>
  typeof value === 'string' || (typeof value === 'object' && value !== null);

/**
 * The ONE place a `ToastCopyMeta` becomes a `toast(...)` call.
 *
 * `error` is supplied by the error paths only; it is what `appendBackendDetail`
 * reads. A success toast passes `undefined`, which makes the flag a no-op
 * there rather than a special case to remember.
 */
const toastFromMeta = (
  copy: ToastCopyMeta | undefined,
  tone: 'success' | 'destructive',
  error?: unknown
) => {
  if (copy === undefined || !isCopySource(copy.title)) return;

  // `extractAxiosErrorDetail` is the app's existing reader for the FastAPI
  // `detail` shapes (bare string, `{ msg | message }` object, 422 array). The
  // delegates this tier absorbs each hand-rolled a SUBSET of it — typically
  // `(error.response?.data as ErrorResponse)?.detail?.msg`. Routing them all
  // through the shared reader is a deliberate WIDENING: a backend that answers
  // with the string or array form now produces a message where the delegate
  // showed nothing. Disclosed rather than discovered; it cannot produce a
  // WORSE toast, only a more specific one, and the empty/whitespace case still
  // resolves to `undefined` so no toast shows a blank description.
  const backendDetail =
    copy.appendBackendDetail === true ? extractAxiosErrorDetail(error) : undefined;
  const base = isCopySource(copy.description) ? resolveCopy(copy.description) : undefined;
  const description =
    backendDetail === undefined
      ? base
      : base === undefined
        ? backendDetail
        : `${base} ${backendDetail}`;

  // `description` is OMITTED rather than passed as `undefined` when the copy
  // has none. Every string reaching this today carries one, so the difference
  // is invisible in product — but the copy-fidelity suite asserts the exact
  // `toast(...)` argument object against what the pre-carve delegate passed,
  // and a stray `description: undefined` key is a difference that suite would
  // (correctly) refuse.
  const title = resolveCopy(copy.title);
  toast(description === undefined ? { tone, title } : { tone, title, description });
};

export const reactQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: GET_GARBAGE_COLLECTION_TIME(),
      // With SSR, we usually want to set some default staleTime
      // above 0 to avoid refetching immediately on the client
      staleTime: 60 * 60 * 1000,
      retry: 1,
    },
  },

  // Two things, in a fixed order.
  //
  // 1. APP-5396, UNCHANGED AND FIRST. A 403 on a team-membership-gated query
  //    means the user lost team access. Re-fetch the joined-teams list (kept
  //    at the global 1h staleTime, since it is mounted for every logged-in
  //    user) so the removed team drops out of the UI without requiring a
  //    manual refresh. It stays ahead of the toast deliberately: it is a data
  //    correction, the toast is only words, and a future `errorToast` on one
  //    of those gated queries must not be able to delay the refetch.
  //
  // 2. The query half of the meta tier (ARCFE-1082). Queries get an
  //    `errorToast` and NOTHING ELSE — see `QueryToastMeta` for why a success
  //    toast on a query is not a thing that can be asked for. Same strictly-
  //    opt-in semantics as the mutation side: no `meta`, no toast.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isForbiddenError(error) && isTeamMembershipGatedQuery(query.queryKey)) {
        void reactQueryClient.invalidateQueries({ queryKey: [GET_JOINED_TEAMS] });
      }
      toastFromMeta(query.meta?.errorToast, 'destructive', error);
    },
  }),

  // Meta-driven toasts. `MutationCache` callbacks receive the mutation itself
  // as their last argument, which is the only place `meta` is reachable from a
  // global handler — a per-mutation `onSuccess` cannot see another mutation's
  // meta, and that is precisely why this wiring is global and singular.
  //
  // ORDERING DELTA, DISCLOSED: TanStack fires the CACHE-level callback BEFORE
  // the mutation's own `options.onSuccess`. Under the pre-meta delegates the
  // toast fired inside `options.onSuccess`, AFTER that hook's
  // `invalidateQueries` calls; it now fires before them. No product behaviour
  // depends on this — the invalidations are `void`-ed and never awaited, so
  // neither ordering blocks the other — but it is a real reordering and is
  // recorded here and in each carved hook's contract note rather than
  // discovered later.
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      toastFromMeta(mutation.meta?.successToast, 'success');
    },
    onError: (error, _variables, _context, mutation) => {
      // The error is threaded through so `appendBackendDetail` has something
      // to read; it is otherwise unused, and a toast without that flag is
      // byte-identical with or without it.
      toastFromMeta(mutation.meta?.errorToast, 'destructive', error);
    },
  }),
  // handle error globally, need investigate more https://stackoverflow.com/questions/69926976/react-query-get-error-status-code-in-global-onerror-handler
  // queryCache: new QueryCache({
  //   onError: (error) => {
  //     logger.debug('error111111', JSON.stringify(error));
  //   },
  // }),
});

// Clear stuck session-creation mutations after persistence rehydration.
//
// Stale `pending` mutations are rehydrated from localStorage by
// `PersistQueryClientProvider`. A hung POST from a prior tab session would
// otherwise pin `useNewSessionMutationPending()` true forever, trapping users
// on the session-page skeleton. Called from `onPersistRestored` *before*
// `resumePausedMutations`, so the hung POST is dropped rather than re-fired.
// Scoped to the session-creation keys to avoid disturbing other in-flight
// mutations.
const STUCK_MUTATION_KEYS_TO_CLEAR: ReadonlyArray<readonly string[]> =
  STUCK_SESSION_CREATION_MUTATION_KEYS;

export function clearStuckSessionCreationMutations() {
  // Guarded so test suites that mock `reactQueryClient` with a partial stub
  // (no `getMutationCache`) don't blow up.
  if (typeof reactQueryClient.getMutationCache !== 'function') return;
  const mutationCache = reactQueryClient.getMutationCache();
  for (const mutationKey of STUCK_MUTATION_KEYS_TO_CLEAR) {
    for (const mutation of mutationCache.findAll({ mutationKey: [...mutationKey] })) {
      mutationCache.remove(mutation);
    }
  }
}
